<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_package.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/process/packing/new_harvested_batch.yml"
  lang="yaml"
></i18n>

<template>
  <div>
    <go-back-link :text="$t('labels.go_back_link')" :name="routeName('show_process')" :params="{
      organization_id: organization_id,
      harvest_process_id: harvest_process_id
    }" />
    <bread-crumbs :labels="[module_name, harvest_process.name]"></bread-crumbs>

    <alert-danger :errors_data="std_err" />

    <div class="mt-8" v-if="load_general_data">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body mb-2 md:mb-0">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.batch_code") }}
                </label>
                <div class="mt-1">
                  <input type="text" v-model.trim="harvested_batch.batch_code"
                    :class="['input-text', isInvalid('batch_code')]" />
                </div>
              </div>
              <div></div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.registered_at") }} (DD/MM/YYYY)
                </label>
                <div class="mt-1">
                  <date-form v-model="harvested_batch.registered_at" :readonly="false" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.week") }}
                </label>
                <div class="mt-1">
                  <input type="text" v-model.trim.number="harvested_batch.week" :class="['input-text', isInvalid('week')]" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.producer") }}
                </label>
                <div class="mt-1">
                  <select-form v-if="producers.length > 0" :options="producers" label="full_label"
                    v-model="harvested_batch.producer_id" :is_invalid="isInvalidSelect('producer')" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_field") }}
                </label>
                <div class="mt-1" v-if="crop_fields.length > 1">
                  <select-form :options="crop_fields" v-model="harvested_batch.crop_field_id"
                    :is_invalid="isInvalidSelect('crop_field')" />
                </div>
                <dd class="content-data" v-else-if="crop_fields.length == 1">
                  {{ crop_fields[0].name }}
                </dd>
                <info-badge class="mt-2" v-if="crop_field?.observations" :label="crop_field?.observations"></info-badge>
              </div>
              <template v-for="(crop_har_package, index) in crop_har_packages" :key="crop_har_package.id">
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_package.number") }} ({{ crop_har_package.name }})
                  </label>
                  <div class="flex mt-1">
                    <input type="number" v-model.number.trim="harvested_packages[index].number"
                      :class="['input-text', isInvalid('number')]" />
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_package.discarded_number") }} ({{ crop_har_package.name }})
                  </label>
                  <div class="flex mt-1">
                    <input type="number" v-model.number.trim="harvested_packages[index].discarded_number"
                      :class="['input-text', isInvalid('number')]" />
                  </div>
                </div>
              </template>
              <div class="sm:col-span-3" v-if="crop_types.length > 1">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_type") }}
                </label>
                <div class="mt-1">
                  <select-form :options="crop_types" v-model="harvested_batch.crop_type_id"
                    :is_invalid="isInvalidSelect('crop_type')" />
                </div>
              </div>
              <div class="sm:col-span-3" v-if="agro_exporter_processes.length > 1">
                <label class="label-form">
                  {{ $t("model.harvested_batch.agro_exporter") }}
                </label>
                <div class="mt-1">
                  <select-form v-if="agro_exporter_processes.length > 1" :options="agro_exporter_processes"
                    v-model="harvested_batch.agro_exporter_process_id" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <harvest-team-input :options="harvest_teams" v-model="harvested_batch.harvest_team_id">
                </harvest-team-input>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.process_line") }}
                </label>
                <div class="mt-1">
                  <select-form :options="process_lines" v-model="harvested_batch.process_line_id" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions space-y-3 my-3 md:space-y-0 md:my-0">
            <white-button @click.prevent="goToShowProcess()" :label="$t('labels.cancel_button')" />
            <span class="space-actions"></span>
            <success-button v-if="!charging_submit" @click.prevent="create()" :label="$t('labels.save_button')" />
            <success-button-processing v-else :label="$t('labels.save_button')" />
          </div>
        </div>
      </div>
    </div>
    <loading-message v-else />
  </div>
</template>

<script>
import RequesterQuery from "@/views/organization/requesters/process/packing/queries_to_harvested_batch.js";
import Requester from "@/views/organization/requesters/process/packing/manage_harvested_batch.js";
import moment from "moment";
import HarvestTeamInput from "@/components/views/layout/harvested_batch/HarvestTeamInput.vue"

export default {
  name: "OrgProcessPackingNewHarvestedBatch",
  components: {
    HarvestTeamInput,
  },
  data() {
    return {
      load_general_data: false,
      charging_submit: false,
      harvest_process: {},
      harvested_batch: {
        batch_code: "",
        total_weight: null,
        registered_at: null,
        crop_type_id: null,
        crop_variety_id: null,
        producer_id: null,
        agro_exporter_process_id: null,
        harvest_team_id: null,
        process_line_id: null,
        crop_field_id: null
      },
      crop_types: [],
      crop_varieties: [],
      agro_exporter_processes: [],
      producers: [],
      crop_har_packages: [],
      harvested_packages: [],
      harvest_teams: [],
      process_lines: [],
      crop_fields: []
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true }
  },
  created() {
    this.checkIfAuthenticated().then(() => {
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => {
        this.harvested_batch.registered_at = moment().format("YYYY-MM-DD");
        this.loadData();
      });
    });
  },
  computed: {
    crop_field: function () {
      return this.crop_fields.find(
        crop_field => crop_field.id == this.harvested_batch.crop_field_id
      );
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchCropTypes();
      await this.fetchAgroExporterProcesses();
      await this.fetchProducers();
      await this.fetchCropHarPackages();
      await this.fetchProcessLines();
      await this.fetchHarvestTeams();
      this.load_general_data = true;
    },
    fetchHarvestProcess: async function () {
      const response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },
    fetchCropTypes: async function () {
      const response = await RequesterQuery.fetchCropTypes(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_types = response.data;
      this.harvested_batch.crop_type_id = this.hasOneItem(this.crop_types);
    },
    fetchHarvestTeams: async function () {
      const response = await RequesterQuery.fetchHarvestTeams(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_teams = response.data;
      this.harvest_teams.forEach(team => {
        team["name"] = team.harvest_team_code;
      });

      if (this.harvest_teams.length == 1) this.harvested_batch.harvest_team_id = this.harvest_teams[0].id;
    },
    fetchAgroExporterProcesses: async function () {
      const response = await RequesterQuery.fetchAgroExporterProcesses(
        this.organization_id,
        this.harvest_process_id
      );
      this.agro_exporter_processes = response.data;
      this.agro_exporter_processes = this.agro_exporter_processes.map(aep => {
        return {
          id: aep.id,
          name: `${aep.name} - ${aep.agro_exporter.name}`
        };
      });
      this.harvested_batch.agro_exporter_process_id = this.hasOneItem(
        this.agro_exporter_processes
      );
    },
    fetchProducers: async function () {
      const response = await RequesterQuery.fetchProducers(
        this.organization_id,
        this.harvest_process_id
      );
      this.producers = response.data;
      this.producers.forEach(producer => {
        producer.full_label = `${producer.organization_producer_code} - ${producer.fullname}`;
      });
    },
    fetchProcessLines: async function () {
      const response = await RequesterQuery.fetchProcessLines(
        this.organization_id,
        this.harvest_process_id
      );
      this.process_lines = response.data;
    },
    fetchCropHarPackages: async function () {
      const response = await RequesterQuery.fetchCropHarPackages(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_har_packages = response.data;
      this.harvested_packages = this.crop_har_packages.map(item => {
        return {
          id: null,
          crop_har_package_id: item.id,
          number: null
        };
      });
    },
    fetchCropFields: async function () {
      const response = await RequesterQuery.fetchCropFields(
        this.organization_id,
        this.harvested_batch.producer_id,
        this.harvest_process_id
      );
      this.crop_fields = response.data;
      this.harvested_batch.crop_field_id = this.hasOneItem(this.crop_fields);
    },
    fetchOrganizationCropField: async function () {
      const response = await RequesterQuery.fetchOrganizationCropField(
        this.organization_id,
        this.harvested_batch.crop_field_id
      )
      let process_line_id = response.data.process_line_id
      if (this.process_lines.map(pl => pl.id).includes(process_line_id)) {
        this.harvested_batch.process_line_id = response.data.process_line_id
      }
    },
    create: async function () {
      this.charging_submit = true;
      try {
        let response = await Requester.create(
          this.organization_id,
          this.harvest_process_id,
          {
            ...this.harvested_batch,
            harvested_packages: this.harvested_packages
          }
        );

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowHarvestedBatch(response.data.harvested_batch_id);
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.charging_submit = false;
    },
    goToShowProcess: function () {
      this.$router.push({
        name: this.routeName("show_process"),
        params: {
          organization_id: this.organization_id,
          harvest_process_id: this.harvest_process_id
        }
      });
    },
    goToShowHarvestedBatch: function (harvested_batch_id) {
      this.$router.push({
        name: this.routeName("show_harvested_batch"),
        params: {
          organization_id: this.organization_id,
          harvested_batch_id: harvested_batch_id,
        },
      });
    },
    hasOneItem: function (array) {
      return array.length == 1 ? array[0].id : null;
    }
  },
  watch: {
    harvested_packages: {
      handler: function () {
        this.harvested_packages.forEach(har_package => {
          if (isNaN(har_package.number)) har_package.number = "";
          if (har_package.number && !isNaN(har_package.number)) {
            har_package.number = Math.abs(Math.floor(har_package.number));
          }
        });
      },
      immediate: true,
      deep: true
    },
    "harvested_batch.producer_id": function (new_value) {
      this.harvested_batch.crop_field_id = null;
      this.harvested_batch.crop_variety_id = null;
      this.crop_fields = [];
      if (new_value) this.fetchCropFields();
    },
    "harvested_batch.crop_field_id": function (new_value) {
      this.harvested_batch.process_line_id = null;
      if (new_value) {
        this.harvested_batch.crop_variety_id = this.crop_field.crop_variety_id;
        this.fetchOrganizationCropField();
      }
    },
    "harvested_batch.registered_at": function (new_value) {
      if (new_value) {
        this.harvested_batch.week = moment(new_value).isoWeek();
      }
    }
  }
};
</script>
